// @spec SCF-005
// @spec IAM-STR-001
// @spec IAM-STR-031
// @spec SHL-STR-022
// @spec SHL-STR-016
// @spec SHL-STR-048
// @spec SHL-STR-047
// @spec SHL-STR-050
// @fixed: removed 'use client' directive (Next.js-specific, not needed for Vite)
// @fixed: added ClerkProvider to wrap children before router initialization

import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { I18nextProvider } from 'react-i18next';

import { BrandingProvider } from '@/components/branding';
import { KeyboardShortcutsDialog } from '@/components/shortcuts';
import { TextScaleProvider } from '@/components/text-scale-provider';
import { AnnouncerProvider } from '@/contexts/AnnouncerContext';
import { ImpersonationProvider } from '@/features/admin/components/impersonation-provider';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import i18n from '@/lib/i18n';
import { createQueryClient } from '@/lib/query-client';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable');
}

export interface ProvidersProps {
  children: React.ReactNode;
}

/**
 * Combined providers wrapper component.
 *
 * Creates QueryClient in React state per TanStack Query best practices
 * to prevent cache sharing in potential SSR scenarios and ensure proper cleanup.
 *
 * Includes:
 * - ClerkProvider for authentication (must wrap components using Clerk hooks)
 * - QueryClientProvider for TanStack Query
 * - I18nextProvider for internationalization (react-i18next)
 * - TextScaleProvider for accessibility text scaling (WCAG 2.1 AA)
 * - AnnouncerProvider for screen reader announcements (WCAG 2.1 AA)
 * - ImpersonationProvider for Super Admin user impersonation
 * - BrandingProvider for organization branding (logo, colors, favicon)
 * - HotkeysProvider for keyboard shortcuts with scope management
 * - KeyboardShortcutsDialog for displaying available shortcuts
 * - ReactQueryDevtools in development mode
 *
 * @example
 * <Providers>
 *   <App />
 * </Providers>
 */
export function Providers({ children }: ProvidersProps) {
  // Create QueryClient in state to prevent re-creation on re-renders
  // and ensure proper cleanup
  const [queryClient] = React.useState(() => createQueryClient());

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      // 'setup-mfa' session task is used by Clerk when MFA is required but not yet
      // configured. The key is not in the TS types for @clerk/clerk-react v5, but is
      // supported at runtime when MFA enforcement is enabled in the Clerk Dashboard.
      taskUrls={{ 'setup-mfa': '/session-tasks/setup-mfa' } as Record<string, string>}
    >
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <TextScaleProvider>
            <AnnouncerProvider>
              <HotkeysProvider initiallyActiveScopes={['global']}>
                <ImpersonationProvider>
                  <BrandingProvider>
                    <KeyboardShortcutsProvider>{children}</KeyboardShortcutsProvider>
                  </BrandingProvider>
                </ImpersonationProvider>
              </HotkeysProvider>
            </AnnouncerProvider>
          </TextScaleProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

/**
 * Internal provider that manages the keyboard shortcuts dialog.
 * Uses the useKeyboardShortcuts hook to register the ? shortcut.
 */
function KeyboardShortcutsProvider({ children }: { children: React.ReactNode }) {
  const { isOpen, closeShortcuts } = useKeyboardShortcuts();

  return (
    <>
      {children}
      <KeyboardShortcutsDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) closeShortcuts();
        }}
      />
    </>
  );
}
